/* Created by Interactive 63 - https://interactive63.com */
/**
 * The Help button and its menu: the shortcut sheet, the first-run tips and the About dialog.
 * The menu is a popover of plain buttons, so it opens with Enter or Space and closes with Escape.
 */
import { PRODUCT_NAME, STUDIO_NAME, STUDIO_URL } from '../../core/branding';
import type { ShortcutsDialog } from '../dialogs/shortcuts-dialog';
import type { Onboarding } from '../onboarding';
import { h } from '../ui/dom';
import { createPopover } from '../ui/popover';
import { showMessageDialog } from '../ui/message-dialog';
import { studioLogo } from '../ui/studio-logo';

export interface HelpMenuDeps {
  shortcuts: ShortcutsDialog;
  onboarding: Onboarding;
}

/** A link to the studio website that opens in a new tab. */
export function studioLink(): HTMLAnchorElement {
  return h('a', { href: STUDIO_URL, target: '_blank', rel: 'noopener' }, STUDIO_NAME);
}

/** Shows the About dialog. */
export function showAbout(): void {
  showMessageDialog(
    `About ${PRODUCT_NAME}`,
    [
      `Version ${import.meta.env.APP_VERSION}.`,
      'Everything runs locally in your browser. Your artwork and projects are never uploaded.',
      h('span', null, 'Made by ', studioLink(), '.'),
    ],
    studioLogo(),
  );
}

/** Creates the Help button with its menu attached to the page. */
export function createHelpMenu(deps: HelpMenuDeps): HTMLButtonElement {
  const button = h(
    'button',
    {
      type: 'button',
      class: 'help-button',
      'aria-label': 'Help',
      'aria-haspopup': 'menu',
      title: 'Help and keyboard shortcuts (H)',
    },
    '?',
  );
  const menu = h('div', { class: 'menu-list', role: 'menu', 'aria-label': 'Help' });
  const popover = createPopover(button, menu, 'Help');

  /** Adds a menu item that closes the menu and then runs the action. */
  const item = (label: string, action: () => void): void => {
    menu.append(
      h(
        'button',
        {
          type: 'button',
          role: 'menuitem',
          onClick: () => {
            popover.close();
            action();
          },
        },
        label,
      ),
    );
  };
  item('Keyboard shortcuts', () => {
    deps.shortcuts.open();
  });
  item('Show tips again', () => {
    deps.onboarding.start();
  });
  item(`About ${PRODUCT_NAME}`, showAbout);
  menu.append(
    h(
      'a',
      { href: STUDIO_URL, target: '_blank', rel: 'noopener', role: 'menuitem' },
      `${STUDIO_NAME} website`,
    ),
  );

  button.addEventListener('click', () => {
    if (popover.isOpen()) popover.close();
    else {
      popover.open();
      menu.querySelector('button')?.focus();
    }
  });
  document.body.append(popover.el);
  return button;
}
